import { Fragment } from 'react'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { Link } from '@tanstack/react-router'
import { XIcon } from 'lucide-react'
import ImageLightboxGallery from '@/components/media/ImageLightboxGallery'
import ImageLightboxPreviewTrigger, {
  createLightboxPreviewItem,
} from '@/components/media/ImageLightboxPreviewTrigger'
import ProductStatusIcon from '@/components/products/ProductStatusIcon'
import SortableListItem from '@/components/patterns/sortable-list-item'
import { Button } from '../ui/button'
import { Separator } from '../ui/separator'
import { Typography } from '@/components/ui/typography'
const CollectionProductsList = ({
  products = [],
  allowReorder = true,
  permission,
  onRemoveProduct,
}) => {
  const path = usePathHelper()
  const previewItems = []

  const productPreviewIndexes = products.map((product) => {
    const previewItem = createLightboxPreviewItem({
      id: `collection-product-${product.id}`,
      media: product?.media?.[0],
      alt: product?.name,
      caption: product?.name,
    })

    if (!previewItem) {
      return null
    }

    const previewIndex = previewItems.length

    previewItems.push(previewItem)

    return previewIndex
  })

  return (
    <ImageLightboxGallery items={previewItems} className="contents">
      {({ getTriggerProps }) => (
        <ul className="my-2 space-y-2">
          {products.map((product, index) => {
            const content = (
              <div className="flex w-full min-w-0 items-center justify-between gap-3 px-2">
                <div className="flex min-w-0 items-center gap-4">
                  <div>
                    <ImageLightboxPreviewTrigger
                      media={product?.media?.[0]}
                      alt={product?.name || 'Product image'}
                      caption={product?.name || 'Product image'}
                      triggerProps={
                        productPreviewIndexes[index] === null
                          ? null
                          : getTriggerProps(productPreviewIndexes[index])
                      }
                      frameClassName="h-[50px] w-[50px] border bg-muted/50 shadow-sm"
                      imageClassName="object-cover"
                    />
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <Link {...path.products.show(product.id).link}>
                      <Typography variant="label" interaction="hoverUnderline">
                        {product.name}
                      </Typography>
                    </Link>
                    <ProductStatusIcon product={product} />
                  </div>
                </div>
                <div className="ml-auto flex shrink-0 items-center justify-end gap-2">
                  {onRemoveProduct ? (
                    <Can do={permission} on="collections">
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => onRemoveProduct(index)}
                      >
                        <XIcon size={16} />
                      </Button>
                    </Can>
                  ) : null}
                </div>
              </div>
            )

            return (
              <Fragment key={product.id}>
                {allowReorder ? (
                  <SortableListItem id={product.id}>{content}</SortableListItem>
                ) : (
                  <li className="inline-flex w-full items-center">{content}</li>
                )}
                {index !== products.length - 1 && <Separator className="my-2" />}
              </Fragment>
            )
          })}
        </ul>
      )}
    </ImageLightboxGallery>
  )
}

export default CollectionProductsList
